import React, { useState, useEffect } from 'react';

const API_URL = '';

export default function SavedSessions({ onClose, onLoadSession }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/calculations`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to load sessions');
      
      const data = await response.json();
      setSessions(data.calculations);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (id) => {
    setDeleting(id);
    try {
      const response = await fetch(`${API_URL}/api/auth/calculations/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      setSessions(sessions.filter(s => s.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-3xl max-h-[85vh] overflow-hidden">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-[#D0FF0B] via-[#0FFFE2] to-[#4A0A66] rounded-2xl opacity-50 blur-sm" />
        
        <div className="relative bg-black/95 rounded-2xl border border-[#4A0A66]/30 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#4A0A66]/30">
            <div>
              <h2 className="text-2xl font-bold text-white">Saved Calculations</h2>
              <p className="text-gray-400 text-sm mt-1">Click any session to load it</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {error && (
              <div className="bg-[#DF1283]/10 border border-[#DF1283]/50 rounded-lg px-4 py-3 text-[#DF1283] text-sm mb-4">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-[#0FFFE2] border-t-transparent rounded-full" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#4A0A66]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-400 mb-2">No saved calculations yet</p>
                <p className="text-gray-500 text-sm">Run a calculation and save it to see it here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="bg-[#0a0014] rounded-xl border border-[#4A0A66]/30 hover:border-[#0FFFE2]/50 transition-all overflow-hidden"
                  >
                    <div 
                      onClick={() => onLoadSession(session)}
                      className="p-4 cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-white font-semibold">{session.name}</h3>
                          <p className="text-gray-500 text-xs">{formatDate(session.created_at)}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSession(session.id);
                          }}
                          disabled={deleting === session.id}
                          className="p-1.5 text-gray-500 hover:text-[#DF1283] transition-colors"
                        >
                          {deleting === session.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                      
                      {/* Quick Stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-black/50 rounded-lg p-2">
                          <p className="text-gray-500 text-xs">Revenue Goal</p>
                          <p className="text-[#D0FF0B] font-semibold">
                            {formatCurrency(session.inputs?.revenueGoal || 0)}
                          </p>
                        </div>
                        <div className="bg-black/50 rounded-lg p-2">
                          <p className="text-gray-500 text-xs">Price/Sale</p>
                          <p className="text-[#0FFFE2] font-semibold">
                            {formatCurrency(session.inputs?.pricePerSale || 0)}
                          </p>
                        </div>
                        <div className="bg-black/50 rounded-lg p-2">
                          <p className="text-gray-500 text-xs">Sales Needed</p>
                          <p className="text-[#DF1283] font-semibold">
                            {session.results?.totalSalesNeeded?.toLocaleString() || '0'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[#4A0A66]/30 flex justify-between items-center">
            <span className="text-sm text-gray-400">
              {sessions.length} saved calculation{sessions.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={fetchSessions}
              className="text-sm text-[#0FFFE2] hover:text-[#D0FF0B] transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
